/*
 * Analytics kit — the app's standard section layout, lifted from Website Analytics
 * (PlunkDocs specs/APP_UI_STANDARD_2026-09-29.md). Every section built after 2026-09-29
 * composes these instead of its own markup:
 *
 *   <div className="territory-view wa-view">
 *     <PageHeader title subtitle chips actions />
 *     <Switcher items active />
 *     <Ribbon tiles comparing note />
 *     <div className="wa-grid"><Card …/></div>
 *   </div>
 *
 * Styles live in sass/v2/_web-activity.scss (the `.wa-*` classes ARE the kit's classes;
 * the prefix is historical). Charts: Sparkline / AreaChart / HBars / Delta from
 * views/web-activity/charts.jsx; Kendo column charts for by-name breakdowns.
 */
import React from "react";
import { NavLink } from "react-router-dom";
import Skeleton from "./skeleton";
import PlunkDropDown from "./plunk-dropdown";
import { Sparkline, AreaChart, HBars, Delta } from "../views/web-activity/charts";
import { seriesCoverage } from "../views/web-activity/data";
export { Sparkline, AreaChart, HBars, Delta, PlunkDropDown, seriesCoverage };

/** Skeleton block while a tile or card loads. */
export const Pending = ({
  h = 160
}) => /*#__PURE__*/React.createElement(Skeleton, {
  height: `${h}px`
});

/** "phase 2" style chip for a column or tile the backend does not serve yet. */
export const Phase = ({
  label = "phase 2"
}) => /*#__PURE__*/React.createElement("span", {
  className: "wa-phase"
}, label);

/**
 * Section header: h1 + one subtitle line (window · comparison · freshness) + chips on the right.
 * `actions` renders after the chips (a primary button, an export link…).
 */
export function PageHeader({
  title,
  subtitle,
  crumbs,
  chips = [],
  actions
}) {
  return /*#__PURE__*/React.createElement("header", {
    className: "wa-hdr"
  }, /*#__PURE__*/React.createElement("div", null, crumbs ? /*#__PURE__*/React.createElement("div", {
    className: "wa-crumbs"
  }, crumbs) : null, /*#__PURE__*/React.createElement("h1", null, title), subtitle ? /*#__PURE__*/React.createElement("div", {
    className: "sub"
  }, subtitle) : null), chips.map((c, i) => c ? /*#__PURE__*/React.createElement("span", {
    className: "wa-chip",
    key: i,
    title: c.title
  }, /*#__PURE__*/React.createElement("span", {
    className: "k"
  }, c.k), " ", c.v) : null), actions ? /*#__PURE__*/React.createElement("span", {
    className: "wa-actions"
  }, actions) : null);
}

/** View switcher under the header: items = [{to, label, isActive?(pathname)}]. */
export function Switcher({
  items,
  ariaLabel = "views"
}) {
  return /*#__PURE__*/React.createElement("nav", {
    className: "wa-switch",
    "aria-label": ariaLabel
  }, items.map(it => /*#__PURE__*/React.createElement(NavLink, {
    key: it.label,
    to: it.to,
    className: "wa-tab",
    activeClassName: "on",
    exact: !!it.exact,
    isActive: it.isActive ? (m, loc) => it.isActive(loc.pathname) : undefined
  }, it.label)));
}

/**
 * KPI ribbon. tiles = [{ l, v, d, s, sp, invert }]:
 *   l label · v formatted value · d deltaOf() result or null · s sub-line · sp sparkline points · invert (down is good)
 * comparing: undefined = comparison still loading · false = none · true = deltas real · null = section has no comparison at all (row hidden)
 * note: one line under the tiles (e.g. the history-coverage guard)
 */
export function Ribbon({
  tiles,
  comparing,
  note,
  loading,
  cols
}) {
  if (loading) return /*#__PURE__*/React.createElement("div", {
    className: "wa-ribbon"
  }, /*#__PURE__*/React.createElement(Pending, {
    h: 110
  }));
  const four = cols === 4 || cols == null && tiles.length === 4;
  // No tile carries a series → compact tiles (no sparkline slot), e.g. the Email section's all-time counts.
  const hasSeries = tiles.some(t => t.sp && t.sp.length > 1 && t.sp.some(v => Number(v) > 0));
  return /*#__PURE__*/React.createElement("div", {
    className: `wa-ribbon${four ? " four" : ""}${cols === 3 ? " three" : ""}${hasSeries ? "" : " compact"}`
  }, tiles.map(t => /*#__PURE__*/React.createElement("div", {
    className: `wa-kpi${t.t ? " has-help" : ""}`,
    key: t.l,
    title: t.t || undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: "l"
  }, t.l), /*#__PURE__*/React.createElement("div", {
    className: "v"
  }, t.v), comparing === null || t.noDelta ? null : /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, comparing === undefined ? /*#__PURE__*/React.createElement("span", {
    className: "wa-d flat"
  }, "\u2026") : /*#__PURE__*/React.createElement(Delta, {
    d: t.d,
    invert: t.invert
  })), /*#__PURE__*/React.createElement("div", {
    className: "s"
  }, t.s || ""), t.sp && t.sp.length > 1 && t.sp.some(v => Number(v) > 0) ? /*#__PURE__*/React.createElement(Sparkline, {
    points: t.sp
  }) : hasSeries ? /*#__PURE__*/React.createElement("div", {
    className: "wa-spark wa-spark-empty"
  }) : null)), note ? /*#__PURE__*/React.createElement("div", {
    className: "wa-note"
  }, note) : null);
}

/** The coverage guard's note for a previous-period series, or null when the comparison is sound. */
export function coverageNote(prevSeries) {
  if (!prevSeries) return null;
  const cov = seriesCoverage(prevSeries);
  return cov < 0.9 ? `No comparison: the tracker's history only covers ${Math.round(cov * 100)}% of the previous period.` : null;
}

/** Card in the 12-column grid. span 4 / 6 / 8 / 12; `more` = right-hand link text with onMore; `right` = any right-hand node. */
export function Card({
  title,
  hint,
  more,
  onMore,
  right,
  phase,
  children,
  span = 12,
  cls = ""
}) {
  return /*#__PURE__*/React.createElement("section", {
    className: `wa-card c${span} ${cls}`
  }, title || hint || more || right ? /*#__PURE__*/React.createElement("h2", null, title, hint ? /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, hint) : null, phase ? /*#__PURE__*/React.createElement(Phase, null) : null, more ? /*#__PURE__*/React.createElement("a", {
    className: "more",
    href: "#",
    onClick: e => {
      e.preventDefault();
      onMore && onMore();
    }
  }, more) : null, right ? /*#__PURE__*/React.createElement("span", {
    className: "more"
  }, right) : null) : null, children);
}

/** Search input for a card toolbar. */
export function Search({
  value,
  onChange,
  placeholder
}) {
  return /*#__PURE__*/React.createElement("input", {
    className: "cust-search wa-search",
    type: "search",
    value: value,
    placeholder: placeholder,
    onChange: e => onChange(e.target.value)
  });
}

/** Right-aligned filter group inside a `.wa-toolbar`. */
export const Filters = ({
  children
}) => /*#__PURE__*/React.createElement("span", {
  className: "wa-filters"
}, children);

/** Segmented toggle: options = [[value, label]]. */
export function Segmented({
  options,
  value,
  onChange
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: "seg"
  }, options.map(([v, l]) => /*#__PURE__*/React.createElement("button", {
    key: v,
    className: value === v ? "on" : "",
    onClick: () => onChange(v)
  }, l)));
}

/** Status pill on the theme tokens: tone = success | warning | danger | info | neutral. */
export function Pill({
  tone = "neutral",
  children
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: `wa-pill ${tone}`
  }, children);
}

/** Honest empty state. */
export const Empty = ({
  children
}) => /*#__PURE__*/React.createElement("div", {
  className: "wa-empty"
}, children);